import hexToRgb from "hex-to-rgb";

import type { Theme } from "~/types/PlatformConfiguration";

function isValidHex(color: string): boolean {
  if (!color) return false;

  // Validate hex values
  if (color.substring(0, 1) === "#") {
    color = color.substring(1);
  } else return false;

  switch (color.length) {
    case 3:
      return /^[0-9A-F]{3}$/i.test(color);
    case 4:
      return /^[0-9A-F]{4}$/i.test(color);
    case 6:
      return /^[0-9A-F]{6}$/i.test(color);
    case 8:
      return /^[0-9A-F]{8}$/i.test(color);
    default:
      return false;
  }
}

/**
 * Based on the brand colours defined for the MWS mobile app theme from `monorepo/frontend/mobile/src/core/constants/theme.ts`
 */
const mwsAppBrandColors = {
  darkGrey: "#1F1E1E",
  mediumGray: "#808080",
  backgroundBlack: "#000000",
  buttonGrey: "#181D27",
};

/**
 * The dark theme is currently hard-coded based on the MWS mobile app theme and is mainly to be used by the app when they want to render a web page for a route
 */
export const darkTheme: Partial<Theme> = {
  "color-1": mwsAppBrandColors.buttonGrey,
  "background-1": mwsAppBrandColors.backgroundBlack,
  "background-2": mwsAppBrandColors.darkGrey,
  "text-1": "#ffffff",
  "text-2": "#595959",
  "text-3": "#767676",
};

/**
 * The light theme is hard-coded and used for the checkout flow, to ensure we don't have a dark checkout for frame the game.
 */
export const lightTheme: Partial<Theme> = {
  "background-1": "#ffffff",
  "background-2": "#f8f8f8",
  "background-3": "#929292",
  "text-1": "#000000",
  "text-2": "#595959",
  "text-3": "#767676",
  "border-1": "#E1DDDC",
  "border-2": "#242424",
};

type PartialPlatformStyling = {
  theme: Partial<Theme> | null;
};

type StyleFromThemeOptions = {
  overwriteTheme: "dark" | "light";
};

/**
 * Function to get css styles that define the css color variables for the platform theme.
 *
 * General usage guidelines for using the colors from the platform theme:
 * - Assume background and text colors have sufficient contrast. E.g. use `text-1` or `text-2` for text color and `background-1` for background color. But not `color-1` on top of `background-1`.
 * - Assume `color-1` and `color-2` have sufficient contrast between each other. E.g. the base-button component can use `--color-1` for background color and `--color-2` for the text color.
 * - Assume the border colors have contrast with the background colors. E.g. `border-1` should be visible on `background-1`.
 * - Always prioritize the semantic meaning of the color variables over the actual color value. E.g. if you want the background in a specific color, and the `border-1` color happens to be that color; still use one of the background colors, and not the border color.
 * In most cases, you can overwrite the color variable for that block or component (header/footer) from the Platform Configuration so you can still achieve the desired color value.
 * - Do not use these variables directly in CSS, but use their Tailwind CSS classes instead. E.g. use `bg-platform-bg-1` instead of `background: var(--background-1)`.
 */
export const useGetStyleFromTheme = () => {
  const getStyleFromTheme = (
    platformStyling: PartialPlatformStyling,
    options?: StyleFromThemeOptions,
  ) => {
    const theme = platformStyling?.theme;

    let platformStyles = "";
    if (!theme) return platformStyles;

    let themeToGenerateTheStylesFrom = theme;

    if (options?.overwriteTheme === "dark") {
      themeToGenerateTheStylesFrom = { ...theme, ...darkTheme };
    } else if (options?.overwriteTheme === "light") {
      themeToGenerateTheStylesFrom = { ...theme, ...lightTheme };
    }

    for (const [key, value] of Object.entries(themeToGenerateTheStylesFrom)) {
      if (value && isValidHex(value)) {
        platformStyles += `--${key}: ${value};`;
        platformStyles += `--${key}-rgb: ${hexToRgb(value)};`;
      }
    }

    return platformStyles;
  };

  return { getStyleFromTheme };
};
